/*
 * Copyright (C) 2019 Spanning Cloud Apps. LLC  All rights reserved.
 */
import * as _l from 'lodash';
import * as moment from 'moment';
import './backupPicker.tpl.html';
import './backupPicker.scss';
import './monthPicker.js';

angular
  .module('shared.backupPicker', ['filters.i18n', 'services.batch', 'services.error', 'shared.monthPicker'])
  .directive('sbsfBackupPicker', directive)
  .controller('BackupPickerCtrl', controller);

/**
 * Defines the sbsf-backup-picker directive.
 *
 * @returns {Object} - The directive settings
 */
function directive() {
  return {
    controller: 'BackupPickerCtrl',
    templateUrl: 'shared/backupPicker/backupPicker.tpl.html',
    restrict: 'E',
    transclude: true,
    scope: {
      // The selected backup batch. We will set this when the user picks a backup from the picker, or it can be
      // set by the caller and we'll display that backup date in the picker.
      batch: '=',
      // Indicates whether failed backups are to be shown in the pick list. The default is false.
      showFailures: '='
    }
  };
}

/**
 * The controller for the directive.
 */
function controller($document, $filter, $q, $scope, $timeout, batchUtils, ErrorService, BACKUP_STATE, BATCH_RESULTS) {
  const DATE_FORMAT = 'llll'; // Thu, Sep 4, 1986 8:30 PM
  const MONTH_FORMAT = 'MMMM YYYY'; // September 1986
  const i18n = $filter('i18n');

  $scope.backupListClicked = backupListClicked;
  $scope.documentClicked = documentClicked;
  $scope.getBackupDateTooltip = getBackupDateTooltip;
  $scope.getBackupList = getBackupList;
  $scope.getBackupStatusClass = getBackupStatusClass;
  $scope.getFormattedMonth = getFormattedMonth;
  $scope.getListBackupDateStr = getListBackupDateStr;
  $scope.getNextBackupTooltip = getNextBackupTooltip;
  $scope.getPrevBackupTooltip = getPrevBackupTooltip;
  $scope.getSelectedBackupDateStr = getSelectedBackupDateStr;
  $scope.hasNextBackup = hasNextBackup;
  $scope.hasNextMonth = hasNextMonth;
  $scope.hasPrevBackup = hasPrevBackup;
  $scope.hasPrevMonth = hasPrevMonth;
  $scope.isSelectedBackup = isSelectedBackup;
  $scope.monthListClicked = monthListClicked;
  $scope.selectBackup = selectBackup;
  $scope.selectNextBackup = selectNextBackup;
  $scope.selectNextMonth = selectNextMonth;
  $scope.selectPrevBackup = selectPrevBackup;
  $scope.selectPrevMonth = selectPrevMonth;
  $scope.toggleBackupList = toggleBackupList;
  $scope.toggleMonthList = toggleMonthList;
  $scope.watchBackupListMoment = watchBackupListMoment;
  $scope.watchForDestroy = watchForDestroy;

  $scope.backupList = [];
  $scope.backupListVisible = false;
  $scope.monthListVisible = false;
  $scope.loadingBackupList = false;

  $scope.watchBackupListMoment();
  $scope.watchForDestroy();

  // Get the range of known backups
  batchUtils.getBackupDetails().then((details) => {
    $scope.firstBatchId = details.backup.first.id;
    $scope.lastBatchId = details.backup.last.id;
    $scope.firstBatchMoment = moment(details.backup.first.timestamp);
    $scope.lastBatchMoment = moment(details.backup.last.timestamp);
  });

  /**
   * Called when the backup list is clicked.
   *
   * @param {object} event - the click event.
   */
  function backupListClicked(event) {
    // This function will get called if the backup list is clicked and no child first handled the event. We will stop
    // propagation so that the document click handler won't be invoked.
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Called when the document is clicked and the click has not been handled by the backup list click handler.
   */
  function documentClicked() {
    let hidden = false;

    // The click was outside the month and backup list, so hide them.
    if ($scope.monthListVisible) {
      $scope.toggleMonthList();
      hidden = true;
    }

    if ($scope.backupListVisible) {
      $scope.toggleBackupList();
      hidden = true;
    }

    if (hidden) {
      // Without the $apply, there is a long delay before the lists actually disappear from the screen.
      $scope.$apply();
    }
  }

  /**
   * Returns the tooltip for the backup date button.
   */
  function getBackupDateTooltip() {
    return i18n($scope.backupListVisible ? 'BACKUP_PICKER_VISIBLE_DATE_TOOLTIP' : 'BACKUP_PICKER_HIDDEN_DATE_TOOLTIP');
  }

  /**
   * Populates the list of backups for the current backup list moment.
   *
   * @returns {Promise} the promise of completion.
   */
  function getBackupList() {
    $scope.loadingBackupList = true;
    return batchUtils
      .getBackupsByMonth($scope.backupListMoment)
      .then((backups) => {
        // Filter out unfinished and failed backups
        $scope.backupList = _l.filter(
          backups,
          (backup) =>
            backup.state === BACKUP_STATE.FINISHED && ($scope.showFailures || backup.status !== BATCH_RESULTS.FAILURE)
        );
      })
      .finally(() => {
        $scope.loadingBackupList = false;
      });
  }

  /**
   * Returns the class to be used to color the status dot next to a backup
   *
   * @param {object} backup - The backup whose status class is to be returned.
   * @returns {String} The CSS class name to be added to the dot.
   */
  function getBackupStatusClass(backup) {
    return backup.status;
  }

  /**
   * Returns the formatted year and month shown in the backup list.
   */
  function getFormattedMonth() {
    return $scope.backupListMoment && $scope.backupListMoment.format(MONTH_FORMAT);
  }

  /**
   * Returns the formatted date string for a backup in the picklist.
   *
   * @param {object} backup - The backup whose date is to be returned.
   * @returns {String} the formatted date string.
   */
  function getListBackupDateStr(backup) {
    return backup ? moment(backup.date).format(DATE_FORMAT) : i18n('SELECT_A_BACKUP');
  }

  /**
   * Returns the tooltip for the next button.
   *
   * @returns {String} the tooltip for the next button.
   */
  function getNextBackupTooltip() {
    return i18n($scope.hasNextBackup() ? 'SELECT_NEXT_BACKUP_TOOLTIP' : 'NO_NEWER_BACKUPS_TOOLTIP');
  }

  /**
   * Returns the tooltip for the prev button.
   *
   * @returns {String} the tooltip for the prev button.
   */
  function getPrevBackupTooltip() {
    return i18n($scope.hasPrevBackup() ? 'SELECT_PREV_BACKUP_TOOLTIP' : 'NO_OLDER_BACKUPS_TOOLTIP');
  }

  /**
   * Returns the formatted date string for the selected backup.
   *
   * @returns {String} the formatted date string.
   */
  function getSelectedBackupDateStr() {
    return $scope.batch ? moment($scope.batch.date).format(DATE_FORMAT) : i18n('SELECT_A_BACKUP');
  }

  /**
   * Determines whether there is a newer backup after the current one.
   *
   * @returns {Boolean} true if there is a newer backup.
   */
  function hasNextBackup() {
    return !!($scope.batch && $scope.lastBatchId && $scope.batch.batchId < $scope.lastBatchId);
  }

  /**
   * Determines whether we can go forward a month in the backup browser.
   *
   * @returns {Boolean} true if there is a newer month.
   */
  function hasNextMonth() {
    // As long as we're not showing the month with the newest backup we can go forward.
    return !!($scope.lastBatchMoment && !$scope.lastBatchMoment.isSame($scope.backupListMoment, 'month'));
  }

  /**
   * Determines whether there is an older backup before the current one.
   *
   * @returns {Boolean} true if there is an older backup.
   */
  function hasPrevBackup() {
    return !!($scope.batch && $scope.firstBatchId && $scope.batch.batchId > $scope.firstBatchId);
  }

  /**
   * Determines whether we can go back a month in the backup browser.
   *
   * @returns {Boolean} true if there is an older month.
   */
  function hasPrevMonth() {
    // As long as we're not showing the month with the oldest backup we can go back.
    return !!($scope.firstBatchMoment && !$scope.firstBatchMoment.isSame($scope.backupListMoment, 'month'));
  }

  /**
   * Determines whether a backup is the selected backup.
   *
   * @param {object} backup - the backup to be checked.
   * @returns {Boolean} true if the backup is selected.
   */
  function isSelectedBackup(backup) {
    return !!($scope.batch && backup && $scope.batch.batchId === backup.batchId);
  }

  /**
   * Called when the month list is clicked.
   *
   * @param {object} event - the click event.
   */
  function monthListClicked(event) {
    // This function will get called if the month list is clicked and no child first handled the event. We will stop
    // propagation so that the document click handler won't be invoked.
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Displays the specified backup batch.
   *
   * @param {object} backup - the backup batch to be displayed.
   * @returns {Promise} the promise of completion
   */
  function selectBackup(backup) {
    if ($scope.isSelectedBackup(backup)) {
      return $q.when();
    }

    $scope.toggleBackupList(); // Hide it

    return batchUtils
      .loadBatch(backup.batchId)
      .then((batch) => {
        $scope.batch = batch;
      })
      .catch((err) => {
        ErrorService.report(err);
      });
  }

  /**
   * Displays the next backup.
   *
   * @returns {Promise} the promise of completion
   */
  function selectNextBackup() {
    return batchUtils
      .getNextBatch($scope.batch.batchId)
      .then((batch) => {
        $scope.batch = batch;
      })
      .catch((err) => {
        ErrorService.report(err);
      });
  }

  /**
   * Displays the next month of backups in the backup list.
   *
   * @returns {Promise} the promise of completion
   */
  function selectNextMonth() {
    $scope.backupList = [];
    $scope.backupListMoment.add(1, 'month');
    $scope.monthListVisible = false;
    return $scope.getBackupList();
  }

  /**
   * Displays the previous backup.
   */
  function selectPrevBackup() {
    return batchUtils
      .getPreviousBatch($scope.batch.batchId)
      .then((batch) => {
        $scope.batch = batch;
      })
      .catch((err) => {
        ErrorService.report(err);
      });
  }

  /**
   * Displays the previous month of backups in the backup list.
   *
   * @returns {Promise} the promise of completion
   */
  function selectPrevMonth() {
    $scope.backupList = [];
    $scope.backupListMoment.subtract(1, 'month');
    $scope.monthListVisible = false;
    return $scope.getBackupList();
  }

  /**
   * Shows or hides the picklist of backups.
   *
   * @returns {Promise} the promise of completion.
   */
  function toggleBackupList() {
    if (!$scope.backupListVisible) {
      // Get the year and month of the currently displayed backup, or the most recent backup if there is no current
      $scope.backupListMoment = $scope.batch ? moment($scope.batch.date) : $scope.lastBatchMoment.clone();
      return $scope.getBackupList().then(() => {
        $scope.backupListVisible = true;
        // Watch for clicks outside the backup list to hide it
        // Without the $timeout, documentClicked gets called right away and immediately hides the backup list
        $timeout(() => {
          $document.on('click', documentClicked);
        });
      });
    } else {
      $scope.backupListVisible = false;
      $scope.monthListVisible = false;
      $document.off('click', documentClicked);
      return $q.when();
    }
  }

  /**
   * Shows or hides the picklist of months.
   */
  function toggleMonthList() {
    $scope.monthListVisible = !$scope.monthListVisible;
  }

  /**
   * Watches for changes to the selected year.
   */
  function watchBackupListMoment() {
    $scope.$watch(
      () => {
        return $scope.backupListMoment && $scope.backupListMoment.valueOf();
      },
      function (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          $scope.getBackupList();
        }
      }
    );
  }

  /**
   * Watches for the scope to be destroyed in order to do some clean-up.
   */
  function watchForDestroy() {
    $scope.$on('$destroy', function () {
      $document.off('click', documentClicked);
    });
  }
}
